import * as React from 'react';
import { css } from '@emotion/css';
import {
  Button,
  Divider,
  Empty,
  List,
  notification,
  Popover,
  Tooltip,
  Typography,
} from 'antd';
import { groupBy, mapValues } from 'lodash';
import { getSavedSearches } from '../../../api/apis';
import Loader from '../../shared/Loader';
import { getFormattedDate, isJson } from '../../../utils/helper';

export interface ISavedSearchesProps {}
const savedSearchesContainer = css`
  .saved-search-items-list {
    .--list-item {
      position: relative;
      padding-bottom: 26px;
      flex-wrap: nowrap;
      .--para-item {
        margin-right: 1rem;
        margin-bottom: 0;
      }
    }
    .--view-source {
      position: absolute;
      right: 8px;
      bottom: 0;
    }
  }
`;

type savedSearchesItemType = Array<Record<string | number | symbol, any>>;

export default function SavedSearches(_props: ISavedSearchesProps) {
  const [data, setData] = React.useState<savedSearchesItemType>([]);
  const [loading, setLoading] = React.useState<boolean>(true);

  const renderSourcePopover = (source: Object) => {
    return (
      <Popover
        autoAdjustOverflow
        content={<pre>{JSON.stringify(source, null, 2)}</pre>}
        title="Search State"
        showArrow={false}
        placement="bottomLeft"
        overlayInnerStyle={{
          overflowX: 'auto',
          maxWidth: '300px',
        }}
      >
        <div className="--view-source">
          <Button type="link">View Search State</Button>
        </div>
      </Popover>
    );
  };
  const renderLists = () => {
    const groupedLists = mapValues(groupBy(data, 'timestampFormatted'));

    const groupedListsHeaders = Object.keys(groupedLists);

    if (groupedListsHeaders.length === 0) {
      return (
        <Empty
          description={
            !loading ? (
              <span>We couldn't find any saved searches!</span>
            ) : (
              <span>Fetching...</span>
            )
          }
        />
      );
    }

    return groupedListsHeaders.map((headerKey, index) => {
      let dataSource: savedSearchesItemType = [];
      dataSource = groupedLists[headerKey];

      if (dataSource.length === 0) return null;

      return (
        <List
          key={headerKey}
          header={
            <Divider style={{ margin: 0 }} plain>
              <b>{headerKey}</b>
            </Divider>
          }
          bordered
          dataSource={dataSource}
          renderItem={({ search_query, timestamp, search_state }) => (
            <List.Item className="--list-item">
              <>
                <Tooltip
                  title={search_query}
                  showArrow={false}
                  overlayInnerStyle={{
                    maxHeight: '70vh',
                    overflowY: 'auto',
                    width: '300px',
                  }}
                >
                  <Typography.Paragraph
                    ellipsis={{ rows: 2, expandable: false }}
                    className="--para-item"
                  >
                    {search_query}
                  </Typography.Paragraph>
                </Tooltip>

                <Tooltip title={timestamp}>
                  <Typography.Text style={{ whiteSpace: 'nowrap' }}>
                    {timestamp}
                  </Typography.Text>
                </Tooltip>
              </>
              {renderSourcePopover(search_state)}
            </List.Item>
          )}
          footer={
            index === groupedListsHeaders.length - 1 ? (
              <Divider plain>That's all folks! 🤐</Divider>
            ) : null
          }
        />
      );
    });
  };
  const fetchSavedSearches = async () => {
    setLoading(true);

    try {
      const savedSsearches = await getSavedSearches();
      setData(
        (savedSsearches.length ? savedSsearches : []).map((item: any) => ({
          ...item,
          search_state: {
            ...item.search_state,
            ...(isJson(item.search_state.body)
              ? { body: JSON.parse(item.search_state.body) }
              : {}),
          },
          timestampFormatted: getFormattedDate(item.timestamp, {
            weekday: 'long',
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          }),
        }))
      );
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Error fetching the saved searches!',
      });
    } finally {
      setLoading(false);
    }
  };
  React.useEffect(() => {
    fetchSavedSearches();
  }, []);

  return (
    <div className={savedSearchesContainer}>
      <Loader loading={loading}>
        <div className="saved-search-items-list">{renderLists()}</div>
      </Loader>
    </div>
  );
}
